import {PublicClientApplication} from '@azure/msal-browser';
import {createWithEqualityFn} from 'zustand/traditional';
import {shallow} from 'zustand/shallow';
import OktaAuth from '@okta/okta-auth-js';

import {FeatureFlag} from 'core/types';
import {EnnablRoleType, EnnablUserRole} from 'modules/decisionsNext/api/types';

import {devtools} from 'utils/zustand';

export type AuthState = {
  authorized: boolean;
  getToken: () => string;
  flags?: FeatureFlag[];
  token: string | null;
  azureAuthInstance?: PublicClientApplication;
  oktaAuthInstance?: OktaAuth;
  userInfo?: {
    id: string;
    ennablEmployeeId: string | null;
    tenantId: string;
    tenantName: string;
    permissions: string[];
    roles: string[];
    roleType: EnnablRoleType;
    role?: EnnablUserRole | null;

    timeZone?: string;
    profile?: {
      email?: string;
      familyName?: string;
      givenName?: string;
      name?: string;
    };
  };
};

export const useAuthStore = createWithEqualityFn<AuthState>()(
  devtools(
    (_, get) => ({
      authorized: false,
      token: null,
      getToken: () => {
        const token = get().token;

        if (!token) {
          throw new Error('Token is not set');
        }

        return token;
      },
    }),
    {
      name: 'Auth',
    }
  ),
  shallow
);

export const setTokenAndAuthorized = (token: AuthState['token']) => {
  useAuthStore.setState({token, authorized: Boolean(token)}, false, 'setToken');
};

export const setAzureAuthInstance = (authInstance: AuthState['azureAuthInstance']) => {
  useAuthStore.setState({azureAuthInstance: authInstance}, false, 'setAzureAuthInstance');
};

export const setOktaAuthInstance = (authInstance: AuthState['oktaAuthInstance']) => {
  useAuthStore.setState({oktaAuthInstance: authInstance}, false, 'setOktaAuthInstance');
};

export const setUserInfo = (userInfo: AuthState['userInfo']) => {
  useAuthStore.setState({userInfo}, false, 'setUserInfo');
};

export const setFeatureFlags = (flags: AuthState['flags']) => {
  useAuthStore.setState({flags}, false, 'setFeatureFlags');
};

export const getFullName = (state: AuthState) =>
  state.userInfo?.profile?.givenName && state.userInfo?.profile?.familyName
    ? `${state.userInfo.profile.givenName} ${state.userInfo.profile.familyName}`
    : state.userInfo?.profile?.name || '';

export const getIsAzureAuth = () => !!useAuthStore.getState().azureAuthInstance;
export const getAzureAuthInstance = () => useAuthStore.getState().azureAuthInstance;

export const getIsOktaAuth = () => !!useAuthStore.getState().oktaAuthInstance;
export const getOktaAuthInstance = () => useAuthStore.getState().oktaAuthInstance;
